import { useEffect, useRef } from 'react';
import { Feature } from 'ol';
import { LineString } from 'ol/geom';
import { toContext } from 'ol/render';
import InfoBox from '@/components/common/InfoBox';
import {
    FeatureColorKey,
    FeatureDirection,
    FeatureRoadType,
    FeatureShowDirectionalArrows,
} from '@/components/map/layers/FeatureLayerType';
import { lineStyle } from '@/components/map/style/LineStyle';
import { VegnettRetning } from '@/state';
import { roadNetLinkColor } from '@/utils/SpatialHelper';

interface SampleProps {
    directionBasis: VegnettRetning;
    direction?: 'MED' | 'MOT';
    roadType?: string;
    connecting?: boolean;
    arrows?: boolean;
}

function LineSample({
    directionBasis,
    direction = 'MED',
    roadType = 'Enkel bilveg',
    connecting = false,
    arrows = false,
}: SampleProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const context = canvasRef.current?.getContext('2d');
        if (!context) return;

        const renderer = toContext(context, { size: [132, 32], pixelRatio: 2 });
        const geometry = new LineString([
            [10, 16],
            [45, 16],
            [122, 16],
        ]);
        const feature = new Feature(geometry);
        feature.set(
            FeatureColorKey,
            roadNetLinkColor(
                {
                    type: connecting ? 'KONNEKTERING' : 'HOVED',
                    direction,
                    metered: true,
                },
                directionBasis
            )
        );
        feature.set(FeatureRoadType, roadType);
        feature.set(FeatureDirection, direction);
        feature.set(FeatureShowDirectionalArrows, arrows);

        for (const style of lineStyle(feature, 1, directionBasis, false, false, arrows)) {
            renderer.setStyle(style);
            const styledGeometry = style.getGeometryFunction()(feature);
            if (styledGeometry) renderer.drawGeometry(styledGeometry);
        }
    }, [directionBasis, direction, roadType, connecting, arrows]);

    return (
        <canvas
            className="c-road-net-legend__sample"
            ref={canvasRef}
            aria-hidden="true"
        />
    );
}

const ROAD_STYLES = [
    { roadType: 'Enkel bilveg', label: 'Bilveg' },
    { roadType: 'Enkel bilveg', label: 'Konnektering', connecting: true },
    { roadType: 'Gang- og sykkelveg', label: 'Gang- og sykkelveg, sykkelveg og gågate' },
    { roadType: 'Gangveg', label: 'Gangveg og fortau' },
    { roadType: 'Gangfelt', label: 'Gangfelt' },
    { roadType: 'Trapp', label: 'Trapp' },
];

export function VegnettLegend() {
    return (
        <InfoBox
            className="c-road-net-legend__trigger"
            portalClassName="c-road-net-legend"
            triggerAriaLabel="Vis tegnforklaring for vegnett"
        >
            <h3>Tegnforklaring for vegnett</h3>
            <ul className="c-road-net-legend__rows" aria-label="Linjetyper">
                {ROAD_STYLES.map(({ roadType, label, connecting }) => (
                    <li key={label}>
                        <LineSample
                            directionBasis={VegnettRetning.GEOMETRI}
                            roadType={roadType}
                            connecting={connecting}
                        />
                        <span>{label}</span>
                    </li>
                ))}
            </ul>
            <ul
                className="c-road-net-legend__rows c-road-net-legend__types"
                aria-label="Farger"
            >
                <p className="c-road-net-legend__description">
                    Pilene viser retningen til veglenken etter valgt retningsgrunnlag. Når
                    retningsgrunnlaget er metreringsretning, vises veglenker som går mot
                    metreringsretningen i rødt.
                </p>
                <li>
                    <LineSample directionBasis={VegnettRetning.METRERING} arrows />
                    <span>Veglenken følger metreringsretningen</span>
                </li>
                <li>
                    <LineSample
                        directionBasis={VegnettRetning.METRERING}
                        direction="MOT"
                        arrows
                    />
                    <span>Veglenken går mot metreringsretningen</span>
                </li>
            </ul>
        </InfoBox>
    );
}
